import React from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  List,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Avatar,
  Table,
  Progress,
  Badge,
  Tooltip,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ReloadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  WarningOutlined,
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Fetch dashboard data (analytics summary + by day)
  const { data: dashboardData, isLoading, refetch } = useQuery('dashboard', async () => {
    const [summaryRes, byDayRes] = await Promise.all([
      api.get('/api/analytics/summary'),
      api.get('/api/analytics/tickets-by-day', { params: { days: 7 } }),
    ]);
    return {
      statusCounts: summaryRes.data.statusCounts,
      totals: summaryRes.data.totals,
      byDay: byDayRes.data.days,
    };
  });

  // Fetch agent performance metrics
  const { data: myPerformance, isLoading: performanceLoading } = useQuery(
    'my-performance',
    () => api.get('/api/dashboard/my-performance').then(res => res.data),
    { enabled: !!user }
  );

  // Fetch team overview (admin only)
  const { data: teamOverview, isLoading: teamLoading, error: teamError } = useQuery(
    'team-overview',
    () => api.get('/api/dashboard/team-overview').then(res => res.data),
    { 
      enabled: isAdmin,
      retry: 1,
      onError: (error) => {
        console.error('Team overview error:', error);
      }
    }
  );

  // Fetch recent tickets
  const { data: recentTickets = [] } = useQuery('recentTickets', async () => {
    const response = await api.get('/api/tickets', {
      params: { limit: 10, sort: 'created_at', order: 'desc' }
    });
    return response.data.tickets;
  });

  // Fetch my tickets
  const { data: myTickets = [] } = useQuery('myTickets', async () => {
    const response = await api.get('/api/tickets', {
      params: { assigned_to: user?.id, limit: 10 }
    });
    return response.data.tickets;
  });

  // Weekly activity from analytics endpoint
  const weeklyActivityData = (dashboardData?.byDay || []).map((d: any) => ({
    name: dayjs(d.day).format('ddd'),
    tickets: d.created,
    solved: d.solved,
  }));

  // My performance trends (last 7 days)
  const myTrendsData = React.useMemo(() => {
    if (!myPerformance?.trends) return [];
    const trendsMap = new Map();
    myPerformance.trends.forEach((t: any) => {
      trendsMap.set(t.day, t.solved_count);
    });
    
    // Fill in missing days with 0
    const result = [];
    for (let i = 6; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
      result.push({
        name: dayjs(date).format('ddd'),
        solved: trendsMap.get(date) || 0,
      });
    }
    return result;
  }, [myPerformance]);

  // Priority breakdown data
  const priorityData = React.useMemo(() => {
    if (!myPerformance?.priorityBreakdown) return [];
    return [
      { name: 'High', value: myPerformance.priorityBreakdown.high || 0, color: '#ff4d4f' },
      { name: 'Medium', value: myPerformance.priorityBreakdown.medium || 0, color: '#faad14' },
      { name: 'Low', value: myPerformance.priorityBreakdown.low || 0, color: '#52c41a' },
    ].filter(item => item.value > 0);
  }, [myPerformance]);

  const getStatusColor = (status: string) => {
    const colors: any = {
      new: 'default',
      open: 'blue',
      pending: 'orange',
      solved: 'green',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'green',
      medium: 'orange',
      high: 'red',
    };
    return colors[priority] || 'default';
  };

  // Chart data
  const ticketStatusData = [
    { name: 'New', value: dashboardData?.statusCounts?.new || 0, color: '#9E9E9E' },
    { name: 'Open', value: dashboardData?.statusCounts?.open || 0, color: '#2196F3' },
    { name: 'Pending', value: dashboardData?.statusCounts?.pending || 0, color: '#FF9800' },
    { name: 'Solved', value: dashboardData?.statusCounts?.solved || 0, color: '#4CAF50' },
  ];

  // Format time helper
  const formatTime = (minutes: number) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Team overview columns
  const teamColumns = [
    {
      title: 'Agent',
      dataIndex: 'first_name',
      key: 'agent',
      render: (_: any, record: any) => (
        <Space>
          <Avatar style={{ backgroundColor: '#1890ff' }}>
            {record.first_name?.[0] || 'A'}
          </Avatar>
          <Text strong>{record.first_name} {record.last_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Open',
      dataIndex: 'open_tickets',
      key: 'open',
      sorter: (a: any, b: any) => a.open_tickets - b.open_tickets,
      render: (value: number) => (
        <Badge count={value} showZero style={{ backgroundColor: '#1890ff' }} />
      ),
    },
    {
      title: 'Solved Today',
      dataIndex: 'solved_today',
      key: 'solved_today',
      sorter: (a: any, b: any) => a.solved_today - b.solved_today,
      render: (value: number) => (
        <Text style={{ color: '#52c41a', fontWeight: 600 }}>{value}</Text>
      ),
    },
    {
      title: 'Solved This Week',
      dataIndex: 'solved_this_week',
      key: 'solved_week',
      sorter: (a: any, b: any) => a.solved_this_week - b.solved_this_week,
      render: (value: number) => (
        <Text style={{ color: '#52c41a', fontWeight: 600 }}>{value}</Text>
      ),
    },
    {
      title: 'Avg Resolution',
      dataIndex: 'avg_resolution_hours',
      key: 'resolution',
      sorter: (a: any, b: any) => (a.avg_resolution_hours || 0) - (b.avg_resolution_hours || 0),
      render: (value: number) => value ? `${value}h` : 'N/A',
    },
    {
      title: 'High Priority',
      dataIndex: 'high_priority_open',
      key: 'high_priority',
      sorter: (a: any, b: any) => a.high_priority_open - b.high_priority_open,
      render: (value: number) => value > 0 ? (
        <Tag color="red">{value}</Tag>
      ) : (
        <Text type="secondary">0</Text>
      ),
    },
  ];

  if (isLoading || performanceLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading dashboard...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Welcome back, {user?.first_name}!
            </Title>
            <Text type="secondary">Here's your support overview for today</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => {
              refetch();
              window.location.reload();
            }}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/workspace/new')}>
              New Ticket
            </Button>
          </Space>
        </div>
      </div>

      {/* Admin Team Overview - Quick View */}
      {isAdmin && (
        <Card 
          title={
            <Space>
              <TeamOutlined />
              <Text strong>Team Performance Overview</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
          extra={
            <Button type="link" onClick={() => navigate('/analytics')}>
              View Detailed Analytics
            </Button>
          }
        >
          {teamError ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Text type="danger">Error loading team data. Please try again.</Text>
            </div>
          ) : (
            <Table
              dataSource={teamOverview?.agents || []}
              columns={teamColumns}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              rowKey="id"
              size="small"
              loading={teamLoading}
              locale={{
                emptyText: (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <Text type="secondary">No agent data available</Text>
                  </div>
                )
              }}
            />
          )}
        </Card>
      )}

      {/* Key Metrics - Enhanced */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: '100%', minHeight: 140 }}>
            <Statistic
              title="My Open Tickets"
              value={myPerformance?.today?.open || myTickets.filter((t: any) => t.status !== 'solved').length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: 8, minHeight: 24 }}>
              {myPerformance?.today?.highPriorityOpen > 0 ? (
                <Tag color="red">
                  <FireOutlined /> {myPerformance.today.highPriorityOpen} High Priority
                </Tag>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>All priorities normal</Text>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: '100%', minHeight: 140 }}>
            <Statistic
              title="Solved Today"
              value={myPerformance?.today?.solved || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: 8, minHeight: 24 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {myPerformance?.thisWeek?.solved || 0} this week
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: '100%', minHeight: 140 }}>
            <Statistic
              title="Avg Resolution Time"
              value={myPerformance?.thisWeek?.avgResolutionHours || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="hours"
            />
            <div style={{ marginTop: 8, minHeight: 24 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                This week
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: '100%', minHeight: 140 }}>
            <Statistic
              title="First Response Time"
              value={formatTime(myPerformance?.thisWeek?.avgFirstResponseMinutes || 0)}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: 8, minHeight: 24 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Average this week
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Performance Trends and Priority Breakdown */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="My Performance Trend (Last 7 Days)" style={{ height: '100%', minHeight: 350 }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={myTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{ fontSize: 12 }} />
                <YAxis style={{ fontSize: 12 }} />
                <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="solved" fill="#52c41a" name="Tickets Solved" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="My Open Tickets by Priority" style={{ height: '100%', minHeight: 350 }}>
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No open tickets</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Priority Widgets - Most Important First */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={8}>
          {/* My Tickets */}
          <Card 
            title="My Tickets" 
            extra={<Button type="link" onClick={() => navigate('/tickets?tab=my')}>View All</Button>}
            style={{ height: '100%', minHeight: 400 }}
            bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
          >
            <List
              dataSource={myTickets.filter((t: any) => t.status !== 'solved').slice(0, 4)}
              renderItem={(ticket: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/workspace/${ticket.id}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#1890ff' }}><UserOutlined /></Avatar>}
                    title={
                      <Space>
                        <Text strong>#{ticket.ticket_number}</Text>
                        <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag color={getStatusColor(ticket.status)} >{ticket.status}</Tag>
                        <Tag color={getPriorityColor(ticket.priority)} >{ticket.priority}</Tag>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {myTickets.filter((t: any) => t.status !== 'solved').length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No assigned tickets</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {/* Unassigned Tickets */}
          <Card 
            title="Unassigned Tickets" 
            extra={<Button type="link" onClick={() => navigate('/tickets?tab=unassigned')}>View All</Button>}
            style={{ height: '100%', minHeight: 400 }}
            bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
          >
            <List
              dataSource={recentTickets.filter((t: any) => !t.assigned_to).slice(0, 4)}
              renderItem={(ticket: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/workspace/${ticket.id}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#ff4d4f' }}>!</Avatar>}
                    title={
                      <Space>
                        <Text strong>#{ticket.ticket_number}</Text>
                        <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag color={getPriorityColor(ticket.priority)} >{ticket.priority}</Tag>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {dayjs(ticket.created_at).fromNow()}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {recentTickets.filter((t: any) => !t.assigned_to).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No unassigned tickets</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {/* SLA Alerts */}
          <Card 
            title="SLA Alerts" 
            extra={<WarningOutlined style={{ color: '#faad14' }} />}
            style={{ height: '100%', minHeight: 400 }}
            bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
          >
            <List
              dataSource={recentTickets.filter((t: any) => {
                const hoursSinceCreated = dayjs().diff(dayjs(t.created_at), 'hours');
                return hoursSinceCreated > 24 && t.status !== 'solved';
              }).slice(0, 4)}
              renderItem={(ticket: any) => {
                const hoursSinceCreated = dayjs().diff(dayjs(ticket.created_at), 'hours');
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/workspace/${ticket.id}`)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#faad14' }}><ExclamationCircleOutlined /></Avatar>}
                      title={
                        <Space>
                          <Text strong>#{ticket.ticket_number}</Text>
                          <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                        </Space>
                      }
                      description={
                        <Space>
                          <Tag color={getPriorityColor(ticket.priority)} >{ticket.priority}</Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {hoursSinceCreated}h old
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
            {recentTickets.filter((t: any) => {
              const hoursSinceCreated = dayjs().diff(dayjs(t.created_at), 'hours');
              return hoursSinceCreated > 24 && t.status !== 'solved';
            }).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No SLA alerts</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts and Performance Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          {/* Weekly Activity Chart */}
          <Card title="Weekly Activity (All Tickets)" style={{ height: '100%', minHeight: 350 }}>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={weeklyActivityData}>
                <defs>
                  <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#52c41a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" style={{ fontSize: 12 }} />
                <YAxis style={{ fontSize: 12 }} />
                <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="tickets" stroke="#1890ff" fillOpacity={1} fill="url(#colorTickets)" name="Created" />
                <Area type="monotone" dataKey="solved" stroke="#52c41a" fillOpacity={1} fill="url(#colorSolved)" name="Solved" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* Overall Ticket Status Overview */}
          <Card title="Overall Ticket Status" style={{ height: '100%', minHeight: 350 }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={ticketStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {ticketStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Recent Tickets */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Recent Tickets" extra={<Button type="link" onClick={() => navigate('/tickets')}>View All</Button>}>
            <List
              dataSource={recentTickets.slice(0, 6)}
              renderItem={(ticket: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/workspace/${ticket.id}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar>{ticket.business_name?.[0] || 'T'}</Avatar>}
                    title={
                      <Space>
                        <Text strong>#{ticket.ticket_number}</Text>
                        <Text ellipsis style={{ maxWidth: 200 }}>{ticket.subject}</Text>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag color={getStatusColor(ticket.status)} >{ticket.status}</Tag>
                        <Tag color={getPriorityColor(ticket.priority)} >{ticket.priority}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(ticket.created_at).fromNow()}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
